import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Button, Icon, Input, Loader, Select } from 'chaoskit/src/components';
import useUpdateEffect from 'react-use/lib/useUpdateEffect';
import {
  connectCurrentRefinements,
  connectHitsPerPage,
  connectInfiniteHits,
  connectSearchBox,
  connectSortBy,
  connectStateResults,
} from 'react-instantsearch-dom';

import useDebounce from '../../hooks/useDebounce';
import mascot from '../../assets/media/logo-mascot.svg';

const Content = ({ searching, error, searchResults, children }) => {
  if (error) {
    return (
      <div className="search__results--none">
        <img src={mascot} alt="Gremlin" />
        <p className="u-textSmall u-textMuted">
          😭 There was an error fetching search results. Please try again later.
        </p>
      </div>
    );
  }

  if (searchResults && searchResults.nbHits === 0) {
    return (
      <div className="search__results--none">
        <img src={mascot} alt="Gremlin" />
        <p className="u-textSmall u-textMuted">No results found.</p>
      </div>
    );
  }

  return (
    <Fragment>
      {searching && (
        <Loader className="u-textPrimary u-textXlarge search__loader" />
      )}
      {children}
    </Fragment>
  );
};

Content.propTypes = {
  searching: PropTypes.bool,
  error: PropTypes.bool,
  searchResults: PropTypes.object,
  children: PropTypes.node.isRequired,
};

export const CustomContent = connectStateResults(Content);

const SearchBox = ({ currentRefinement, refine, placeholderText }) => {
  const [searchValue, setSearchValue] = useState(currentRefinement);

  // Debounce input changes
  const debouncedSearchValue = useDebounce(searchValue, 150);

  useUpdateEffect(() => {
    refine(debouncedSearchValue);
  }, [debouncedSearchValue]);

  return (
    <Fragment>
      <Input
        type="search"
        name="query"
        value={searchValue}
        onChange={(name, value) => setSearchValue(value)}
        prefixIcon="search"
        className={cx({ 'has-query': searchValue.length !== 0 })}
        placeholder={placeholderText}
      />
      {searchValue.length !== 0 && (
        <Button
          size="xsmall"
          onClick={() => {
            setSearchValue('');
            refine('');
          }}
          iconOnly
          title="Reset query"
          className="search__resetQueryButton"
        >
          <Icon icon="close" />
        </Button>
      )}
    </Fragment>
  );
};

SearchBox.propTypes = {
  currentRefinement: PropTypes.string,
  refine: PropTypes.func,
  placeholderText: PropTypes.string.isRequired,
};

export const CustomSearchBox = connectSearchBox(SearchBox);

const ClearRefinements = ({ items, refine }) =>
  items.length > 0 && (
    <Button size="xsmall" type="default" onClick={() => refine(items)}>
      <Icon icon="close" className="u-mr--xsmall" /> Clear Filters
    </Button>
  );

ClearRefinements.propTypes = {
  items: PropTypes.array,
  refine: PropTypes.func,
};

export const CustomClearRefinements = connectCurrentRefinements(
  ClearRefinements
);

const HitsPerPage = ({ items, refine, createURL, currentRefinement }) => {
  const options = [];

  items.map(item => options.push({ value: item.value, label: item.label }));

  return (
    <Select
      name="hitsPerPage"
      defaultValue={currentRefinement}
      options={options}
      onChange={(name, value) => {
        refine(value);
        createURL(value);
      }}
    />
  );
};

HitsPerPage.propTypes = {
  items: PropTypes.array,
  refine: PropTypes.func,
  createURL: PropTypes.func,
  currentRefinement: PropTypes.number,
};

export const CustomHitsPerPage = connectHitsPerPage(HitsPerPage);

const SortBy = ({ items, refine, createURL, currentRefinement }) => {
  const options = [];

  items.map(item => options.push({ value: item.value, label: item.label }));

  return (
    <Select
      name="sortBy"
      defaultValue={currentRefinement}
      options={options}
      onChange={(name, value) => {
        refine(value);
        createURL(value);
      }}
    />
  );
};

SortBy.propTypes = {
  items: PropTypes.array,
  refine: PropTypes.func,
  createURL: PropTypes.func,
  currentRefinement: PropTypes.string,
};

export const CustomSortBy = connectSortBy(SortBy);

const InfiniteHits = ({
  hits,
  hasPrevious,
  refinePrevious,
  hasMore,
  refineNext,
  hitTemplate,
  gridClasses,
}) => {
  const HitComponent = hitTemplate;
  return (
    <div className="search__results-container">
      <CustomContent>
        {hasPrevious && (
          <div className="u-mb--large u-textCenter">
            <Button size="small" type="secondary" onClick={refinePrevious}>
              Show Previous
            </Button>
          </div>
        )}
        {hits.length > 0 && (
          <ul className={cx('blockGrid blockGrid--deepMatch', gridClasses)}>
            {hits.map(hit => (
              <li key={hit.slug}>
                <HitComponent hit={hit} />
              </li>
            ))}
          </ul>
        )}
        <div className="u-mt--large u-textCenter">
          <Button
            size="small"
            type="secondary"
            disabled={!hasMore}
            onClick={refineNext}
          >
            {hasMore ? 'Show More' : "🎉 That's all!"}
          </Button>
        </div>
      </CustomContent>
    </div>
  );
};

InfiniteHits.propTypes = {
  hits: PropTypes.array,
  hasPrevious: PropTypes.bool,
  hasMore: PropTypes.bool,
  refinePrevious: PropTypes.func,
  refineNext: PropTypes.func,
  hitTemplate: PropTypes.func.isRequired,
  gridClasses: PropTypes.string.isRequired,
};

export const CustomInfiniteHits = connectInfiniteHits(InfiniteHits);
